import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

/**
 * A custom element that allows the user to clean a search input.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchPageInput - The search input element.
 * @extends {Component<Refs>}
 */
class SearchPageInputComponent extends Component {
  requiredRefs = ['searchPageInput'];

  /** @type {AbortController | undefined} */
  #liveAbort;

  connectedCallback() {
    super.connectedCallback();
    this.refs.searchPageInput.addEventListener('input', this.#handleLiveInput);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.refs.searchPageInput?.removeEventListener('input', this.#handleLiveInput);
    this.#liveAbort?.abort();
  }

  /**
   * Kyren: refresh the results as the shopper types, without pressing search.
   */
  #handleLiveInput = debounce(() => {
    const input = this.refs.searchPageInput;
    const form = input.form;
    if (!form) return;

    const params = new URLSearchParams(new FormData(form));
    params.set('q', input.value.trim());
    const url = `${form.getAttribute('action') || '/search'}?${params.toString()}`;

    this.#liveAbort?.abort();
    this.#liveAbort = new AbortController();

    fetch(url, { signal: this.#liveAbort.signal })
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const ownSection = this.closest('.shopify-section');

        // Swap every other section of the search page (results, filters, extras)
        document.querySelectorAll('main .shopify-section').forEach((section) => {
          if (section === ownSection || !section.id) return;
          const fresh = doc.getElementById(section.id);
          if (fresh) section.replaceWith(document.importNode(fresh, true));
        });

        // Update the "no results" message that sits under the input
        const currentNoResults = form.querySelector('.search-results__no-results');
        const freshForm = ownSection?.id ? doc.getElementById(ownSection.id)?.querySelector('form') : null;
        const freshNoResults = freshForm?.querySelector('.search-results__no-results');
        if (currentNoResults && !freshNoResults) currentNoResults.remove();
        if (freshNoResults) {
          if (currentNoResults) currentNoResults.replaceWith(document.importNode(freshNoResults, true));
          else form.appendChild(document.importNode(freshNoResults, true));
        }

        history.replaceState(history.state, '', url);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.error(error);
      });
  }, 300);

  /**
   * Handles the keydown event on the search input and resets the search when
   * empty and Escape is pressed.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown = debounce((event) => {
    const value = this.refs.searchPageInput.value.trim();

    if (event.key === 'Escape' && value === '') {
      this.#submitEmptySearch();
    }
  }, 100);

  #submitEmptySearch() {
    const searchInput = this.refs.searchPageInput;

    searchInput.focus();
    searchInput.value = '';

    if (this.#isEmptyState()) return;

    searchInput.form?.submit();
  }

  #isEmptyState = () => {
    const url = new URL(window.location.href);
    const queryParam = url.searchParams.get('q') ?? '';

    return queryParam.trim() === '';
  };
}

if (!customElements.get('search-page-input-component')) {
  customElements.define('search-page-input-component', SearchPageInputComponent);
}
